import React from "react";

import { ButtonProps } from "@/components/ui/Button";
import { DropdownSelectItem } from "@/components/ui/Dropdown/DropdownSelect";
import { IconButtonProps } from "@/components/ui/IconButton";
import { ExpandMore20 } from "@/components/ui/SvgIcon";

import cn from "@/utils/cn";

export function renderTrigger({
  children,
  noChevron,
  isOpen,
  onClick,
  chevronClassName = "",
}: {
  children: React.ReactElement;
  noChevron?: boolean;
  isOpen: boolean;
  onClick: () => void;
  chevronClassName?: string;
}) {
  if (typeof children.type === "object") {
    if (
      (children as React.FunctionComponentElement<HTMLButtonElement>).type
        .displayName === "Button"
    ) {
      return React.cloneElement<ButtonProps>(children, {
        endIcon: noChevron ? undefined : (
          <ExpandMore20
            className={cn(chevronClassName, isOpen && "rotate-180")}
          />
        ),
        onClick,
        onKeyDown: (e) => (e.key === "Enter" || e.key === " ") && onClick(),
        "data-testid": "dd-trigger",
      });
    }
    if (
      (children as React.FunctionComponentElement<HTMLButtonElement>).type
        .displayName === "IconButton"
    ) {
      return React.cloneElement<ButtonProps>(children, {
        onClick,
        "data-testid": "dd-trigger",
      });
    }

    if (
      (children as React.FunctionComponentElement<HTMLButtonElement>).type
        .displayName === "Badge"
    ) {
      return React.cloneElement<ButtonProps>(children, {
        onClick,
        className: "cursor-pointer",
        "data-testid": "dd-trigger",
        endIcon: noChevron ? undefined : isOpen ? (
          <ExpandMore20 className="rotate-180" />
        ) : (
          <ExpandMore20 />
        ),
      });
    }

    if (
      (children as React.FunctionComponentElement<HTMLButtonElement>).type
        .displayName === "Input"
    ) {
      return React.cloneElement<ButtonProps>(children, {
        type: undefined,
        className: cn(
          {
            "shadow-focus ring-chorus-teal-200": isOpen,
          },
          "caret-transparent focus:outline-none",
          children.props.className,
        ),
        children: React.cloneElement<IconButtonProps>(children.props.children, {
          className: isOpen ? "rotate-180" : "",
          tabIndex: -1,
        }),
        "data-testid": "dd-trigger",
      });
    }
    throw Error("Trigger element is not supported");
  } else {
    throw Error("Dropdown children must be a ReactElement");
  }
}

export function handleChangeMultiple(
  item: DropdownSelectItem,
  setSelectedState: React.Dispatch<React.SetStateAction<DropdownSelectItem[]>>,
  cb?: (items: DropdownSelectItem[]) => void,
) {
  let result: DropdownSelectItem[];
  setSelectedState((prev) => {
    if (!prev?.length) {
      result = [item];
      return result;
    }
    const found = prev.find((i) => i.key === item.key);

    if (found) {
      result = prev.filter((i) => i.key !== item.key);
      return result;
    } else {
      result = [...prev, item];
      return result;
    }
  });
  // @ts-ignore
  cb && cb(result);
}

export function handleChangeSingle(
  item: Maybe<DropdownSelectItem>,
  set: React.Dispatch<React.SetStateAction<Maybe<DropdownSelectItem>>>,
) {
  set(item);
}

export function getLabel(
  items: DropdownSelectItem[],
  entity: string,
  fallback?: string,
) {
  if (!items.length) return fallback || "Select";
  if (items.length === 1) return items[0].label;
  if (items.length) return `${items.length} ${entity}`;
}
